<template>
  <nav class="overflow-auto p-sm -m-sm">
    <ul class="flex justify-between xl:justify-start space-x-1">
      <li
        v-for="link in links"
        :key="link.id"
        class="flex-none"
        :class="route().current('explore-data') && link.id === 'about' && 'hidden'"
      >
        <button
          :class="[
            'inline-flex text-sm rounded-md px-sm py-medium border-2 border-gray-50 outline-none ring-primary-500',
            'sm:text-base',
            'md:px-base',
            'focus-visible:ring-2',
            reportStore.currentTab === link.id ? 'text-primary-900 bg-primary-100 active:bg-primary-200' : 'hover:bg-primary-50 active:bg-primary-100 body-text-branding',
            route().current('public.reports.show') && reportStore.currentTab === link.id && 'active-tab',
          ]"
          :style="reportStore.currentTab === link.id && route().current('public.reports.show') ? activeTabStyles : null"
          @click="changeTab(link.id, link.label)"
        >
          {{ link.label }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { route } from 'ziggy-js';
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useReportStore } from '@/Stores/Report';
import { trackEvent } from '@/Support/MixpanelTracking';
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import ReportTab = App.Enums.ReportTab;

const reportStore = useReportStore();
const page = usePage();

const props = defineProps<{
  brandSettings?: BrandSettingsData;
}>();

type LinkType = {
  id: ReportTab;
  label: string;
  order?: number;
  display: boolean;
};

const links = computed(() => [
  {
    id: 'overview',
    label: 'Property Data',
    order: reportStore.overview?.order,
    display: reportStore.getSectionDisplay('overview'),
  },
  {
    id: 'comparables',
    label: 'Comparables',
    order: reportStore.comparables.order,
    display: reportStore.getSectionDisplay('comparables'),
  },
  {
    id: 'localArea',
    label: 'Local Area',
    order: reportStore.localArea?.order,
    display: reportStore.getSectionDisplay('localArea'),
  },
  {
    id: 'gallery',
    label: 'Gallery',
    order: reportStore.gallery.order,
    display: reportStore.getSectionDisplay('gallery'),
  },
  {
    id: 'market',
    label: 'Market Data',
    order: reportStore.market.order,
    display: reportStore.getSectionDisplay('market'),
  },
  {
    id: 'about',
    label: 'About',
    order: reportStore.about.order,
    display: true,
  },
].filter((link) => link.display).sort((a, b) => (a.order - b.order)) as LinkType[]);

const activeTabStyles = ref({
  '--fontFamily': `${props.brandSettings?.text.body.fontFamily}`,
  '--textColor': `${props.brandSettings?.buttons.primary.colour}`,
  '--backgroundColor': `${props.brandSettings?.buttons.primary.backgroundColour}`,
});

const changeTab = (tabId: ReportTab, tabName: string) => {
  reportStore.setCurrentTab(tabId);
  trackEvent('Property Report', {
    Status: `${tabName} tab selected`,
    'Property ID': reportStore.propertyId,
    Account: reportStore.accountId,
    Company: page.props.auth.user?.company?.name ?? null,
  });
};
</script>

<style scoped>
.active-tab {
  color: var(--textColor);
  background-color: var(--backgroundColor);
  font-family: var(--fontFamily);
}
</style>
